import { hasCardNumber } from '../../utils/cardNumber'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { FC } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { api } from '../../utils/api'
import { createIdempotencyKey, isResponseError } from '../../utils/idempotency'
import { useCurrency } from '../../contexts/CurrencyContext'
import { compareVariantOrder } from '../../utils/variantOrdering'
import { getQuantityRangeWarning, normalizeQuantityDraft, parseQuantityDraft } from '../../utils/quantityInput'
import { normalizePrintingLabel } from '../../utils/variantLabeling'
import { PriceHistoryChart } from '../charts/PriceHistoryChart'
import { windowEvents } from '../../constants/events'
import { formatSetCode } from '../../utils/setCode'
import styles from './CardPricingInsightsModal.module.css'

export type CardPricingVariant = {
  id: string
  printing: string
  condition: string
  priceCents: number
}

export type CardPricingModalCard = {
  id: string
  name: string
  setId: string
  setName: string
  setCode?: string
  setNumber: string
  gameName: string
  rarity?: string
  imageUrl: string | null
  variants: CardPricingVariant[]
  defaultPriceCents?: number
}

interface CardPricingInsightsModalProps {
  open: boolean
  card: CardPricingModalCard | null
  onClose: () => void
}


const pickBestVariant = (variants: CardPricingVariant[]): CardPricingVariant | null => {
  if (variants.length === 0) return null

  return variants.find((variant) => variant.condition.toLowerCase() === 'near mint' && variant.priceCents > 0)
    || variants.find((variant) => variant.priceCents > 0)
    || variants[0]
}

const normalizeVariants = (card: CardPricingModalCard | null): CardPricingVariant[] => {
  if (!card) return []
  const variants = Array.isArray(card.variants) ? card.variants : []

  if (variants.length > 0) {
    const normalized = variants
      .map((variant, index) => ({
        id: variant.id || `${card.id}-${index}`,
        printing: normalizePrintingLabel(variant.printing || 'Normal'),
        condition: variant.condition || 'Near Mint',
        priceCents: Number.isFinite(variant.priceCents) ? Math.max(0, Math.round(variant.priceCents)) : 0,
      }))

    const deduped = new Map<string, CardPricingVariant>()
    for (const variant of normalized) {
      const key = `${String(variant.printing || '').toLowerCase()}|${String(variant.condition || '').toLowerCase()}`
      const existing = deduped.get(key)
      if (!existing || (existing.priceCents <= 0 && variant.priceCents > 0)) {
        deduped.set(key, variant)
      }
    }

    return [...deduped.values()].sort(compareVariantOrder)
  }

  return [{
    id: `${card.id}-default`,
    printing: 'Normal',
    condition: 'Near Mint',
    priceCents: Number.isFinite(card.defaultPriceCents) ? Math.max(0, Math.round(card.defaultPriceCents || 0)) : 0,
  }]
}

export const CardPricingInsightsModal: FC<CardPricingInsightsModalProps> = ({ open, card, onClose }) => {
  const { formatPrice, currency, baseCurrency, tryConvertPrice } = useCurrency()
  const [selectedVariantId, setSelectedVariantId] = useState('')
  const [quantityInput, setQuantityInput] = useState('1')
  const [manualPrice, setManualPrice] = useState('')
  const [adding, setAdding] = useState(false)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const addInFlightRef = useRef(false)
  // Reused across retries when the outcome was unknown (network error/timeout),
  // so a replay cannot add the same stock twice.
  const idempotencyKeyRef = useRef<string | null>(null)

  const variants = useMemo(() => normalizeVariants(card), [card])
  const printings = useMemo(() => [...new Set(variants.map((variant) => variant.printing))], [variants])

  useEffect(() => {
    if (!open || !card) return
    setSelectedVariantId(pickBestVariant(variants)?.id || '')
    setQuantityInput('1')
    setManualPrice('')
    setFeedback(null)
    idempotencyKeyRef.current = null
  }, [open, card, variants])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  const selectedVariant = useMemo(() => {
    return variants.find((variant) => variant.id === selectedVariantId) || variants[0] || null
  }, [variants, selectedVariantId])

  const visibleVariants = useMemo(() => {
    if (!selectedVariant) return variants
    return variants.filter((variant) => variant.printing === selectedVariant.printing)
  }, [variants, selectedVariant])

  const manualCents = useMemo(() => {
    const raw = Number(manualPrice)
    if (!Number.isFinite(raw) || raw <= 0) return null
    const displayCents = Math.round(raw * 100)
    // Computed during render, so it must not throw; with no rate the typed price is not used
    // (the add then has no price and is refused) rather than converted at a guessed rate.
    return tryConvertPrice(displayCents, currency, baseCurrency)
  }, [manualPrice, currency, baseCurrency, tryConvertPrice])

  const parsedQuantity = parseQuantityDraft(quantityInput)
  const quantityWarning = parsedQuantity === null ? getQuantityRangeWarning() : null

  const unitPriceCents = selectedVariant
    ? (selectedVariant.priceCents > 0 ? selectedVariant.priceCents : (manualCents || 0))
    : 0

  const totalCents = unitPriceCents * (parsedQuantity || 0)

  const handleAddToInventory = async () => {
    if (!card || !selectedVariant) return

    if (!card.setId) {
      setFeedback({ type: 'error', message: 'This card is missing set information and cannot be added right now.' })
      return
    }

    if (unitPriceCents <= 0) {
      setFeedback({ type: 'error', message: 'Please select a priced variant or enter a manual price.' })
      return
    }

    if (parsedQuantity === null) {
      setFeedback({ type: 'error', message: getQuantityRangeWarning() })
      return
    }

    if (addInFlightRef.current) return
    addInFlightRef.current = true

    try {
      setAdding(true)
      setFeedback(null)

      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = createIdempotencyKey('insightsadd')
      }

      const response = await api.post(`/sets/${card.setId}/inventory/bulk-add`, {
        items: [{
          cardId: card.id,
          variantData: {
            variantId: selectedVariant.id,
            printing: selectedVariant.printing,
            condition: selectedVariant.condition,
          },
          quantity: parsedQuantity,
          priceCents: unitPriceCents,
          // The typed price is a manual override — pinned server-side so price
          // tracking can't replace it; the display amount uploads verbatim
          // when it matches the store currency.
          ...(selectedVariant.priceCents <= 0 && manualCents
            ? {
                manualPriceCents: manualCents,
                manualPriceDisplayCents: Math.round(Number(manualPrice) * 100),
                manualPriceDisplayCurrency: currency,
              }
            : {}),
          idempotencyKey: idempotencyKeyRef.current,
        }],
      })

      // Outcome known — next intentional add must use a fresh key.
      idempotencyKeyRef.current = null

      const result = response.data?.data || {}
      const created = Number(result.created) || 0
      const updated = Number(result.updated) || 0
      const alreadyApplied = Number(result.alreadyApplied) || 0
      const errors = Array.isArray(result.errors) ? result.errors : []

      if (created + updated + alreadyApplied <= 0 && errors.length > 0) {
        setFeedback({ type: 'error', message: errors[0]?.error || 'Failed to add to inventory.' })
        return
      }

      window.dispatchEvent(new Event(windowEvents.inventoryUpdated))
      setFeedback({ type: 'success', message: `Added ${parsedQuantity}x ${card.name} to inventory.` })
    } catch (error: any) {
      if (isResponseError(error)) {
        idempotencyKeyRef.current = null
      }
      setFeedback({ type: 'error', message: error?.response?.data?.message || 'Failed to add to inventory.' })
    } finally {
      addInFlightRef.current = false
      setAdding(false)
    }
  }

  if (!open || !card) return null

  return createPortal(
    <div className={styles.backdrop} onClick={onClose} role="dialog" aria-modal="true" aria-label="Card pricing insights">
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>

        <div className={styles.header}>
          <div className={styles.imageWrap}>
            {card.imageUrl ? <img src={card.imageUrl} alt={card.name} className={styles.image} /> : <div className={styles.noImage}>No Image</div>}
          </div>
          <div className={styles.titleBlock}>
            <div className={styles.eyebrow}>Card Pricing & Insights</div>
            <h2 className={styles.title}>{card.name}</h2>
            <p className={styles.meta}>
              {card.setName}{formatSetCode(card.setCode) ? ` (${formatSetCode(card.setCode)})` : ''} {hasCardNumber(card.setNumber) && <>• #{card.setNumber} </>}• {card.gameName}
            </p>
          </div>
        </div>

        <div className={styles.content}>
          <section className={styles.leftPane}>
            <h3 className={styles.sectionTitle}>Variants & Pricing</h3>

            {/* Step 1 — Printing selector (only shown when there are multiple printings) */}
            {printings.length > 1 && (
              <div className={styles.printingSelector}>
                {printings.map((printing) => (
                  <button
                    key={printing}
                    type="button"
                    className={`${styles.printingPill} ${selectedVariant?.printing === printing ? styles.printingPillActive : ''}`}
                    onClick={() => {
                      const candidates = variants.filter((variant) => variant.printing === printing)
                      const best = pickBestVariant(candidates)
                      if (best) setSelectedVariantId(best.id)
                    }}
                  >
                    {printing}
                  </button>
                ))}
              </div>
            )}

            {/* Step 2 — Condition selector (filtered to selected printing) */}
            <div className={styles.variantList}>
              {visibleVariants.map((variant) => {
                  const active = variant.id === selectedVariantId
                  return (
                    <button
                      key={variant.id}
                      type="button"
                      className={`${styles.variantRow} ${active ? styles.variantRowActive : ''}`}
                      onClick={() => setSelectedVariantId(variant.id)}
                    >
                      <span>{variant.condition}</span>
                      <strong>{variant.priceCents > 0 ? formatPrice(variant.priceCents) : 'Manual'}</strong>
                    </button>
                  )
                })
              }
            </div>

            {selectedVariant && selectedVariant.priceCents <= 0 && (
              <label className={styles.manualPriceField}>
                Manual price (each)
                <input
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={manualPrice}
                  onChange={(event) => setManualPrice(event.target.value)}
                  placeholder="e.g. 4.99"
                />
              </label>
            )}

            <div className={styles.addRow}>
              <label className={styles.qtyField}>
                Qty
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={quantityInput}
                  onChange={(event) => setQuantityInput(normalizeQuantityDraft(event.target.value))}
                />
              </label>

              <div className={styles.totals}>
                <div>Unit: {unitPriceCents > 0 ? formatPrice(unitPriceCents) : '—'}</div>
                <div>Total: {totalCents > 0 ? formatPrice(totalCents) : '—'}</div>
              </div>
            </div>

            {quantityWarning && (
              <div className={styles.quantityWarning}>{quantityWarning}</div>
            )}

            {feedback && (
              <div className={feedback.type === 'success' ? styles.feedbackSuccess : styles.feedbackError}>
                {feedback.message}
              </div>
            )}

            <button type="button" className={styles.addButton} onClick={() => void handleAddToInventory()} disabled={adding || !selectedVariant || parsedQuantity === null || unitPriceCents <= 0}>
              {adding ? 'Adding…' : 'Add to Inventory'}
            </button>
          </section>

          <section className={styles.rightPane}>
            <PriceHistoryChart
              cardId={card.id}
              title="Market Price Trend"
              days={180}
              printing={selectedVariant?.printing || null}
              condition={selectedVariant?.condition || null}
              variantId={null}
              chartHeight={220}
            />
          </section>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default CardPricingInsightsModal
